import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useCurrentUser, useLogout } from "@/hooks/useAuth";

/** Top-bar control: "Login" link when anonymous, user dropdown when signed in. */
export function AuthMenu() {
  const { data: user, isLoading } = useCurrentUser();
  const { mutate: logout } = useLogout();
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  if (isLoading) {
    return <div className="w-16 h-6 bg-surface-2 rounded animate-pulse" />;
  }

  if (!user) {
    return (
      <div className="flex items-center gap-2 text-xs">
        <Link
          to="/login"
          className="text-zinc-400 hover:text-accent-light transition-colors"
        >
          Login
        </Link>
        <span className="text-zinc-700">·</span>
        <Link
          to="/register"
          className="text-zinc-400 hover:text-accent-light transition-colors"
        >
          Register
        </Link>
      </div>
    );
  }

  return (
    <div ref={menuRef} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-zinc-300 hover:text-white bg-surface-2 hover:bg-surface-3 px-3 py-1.5 rounded transition-colors"
      >
        <span className="font-mono">{user.username}</span>
        {user.is_admin && (
          <span className="text-[9px] uppercase tracking-wider text-accent-light">
            admin
          </span>
        )}
        <span className="text-zinc-600">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 min-w-[160px] bg-surface-1 border border-surface-3 rounded shadow-xl py-1 z-50">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-600 border-b border-surface-3">
            Signed in
          </div>
          <Link
            to="/favorites"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-xs text-zinc-300 hover:bg-surface-2 transition-colors"
          >
            ♥ Favorites
          </Link>
          <Link
            to="/saved-searches"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-xs text-zinc-300 hover:bg-surface-2 transition-colors"
          >
            ★ Saved searches
          </Link>
          <Link
            to="/settings"
            onClick={() => setOpen(false)}
            className="block px-3 py-2 text-xs text-zinc-300 hover:bg-surface-2 transition-colors"
          >
            ⚙ Settings
          </Link>
          {user.is_admin && (
            <>
              <div className="border-t border-surface-3 my-1" />
              <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-600">
                Admin
              </div>
              <Link
                to="/admin/analytics"
                onClick={() => setOpen(false)}
                className="block px-3 py-2 text-xs text-zinc-300 hover:bg-surface-2 transition-colors"
              >
                📊 Analytics
              </Link>
              <Link
                to="/admin/stats"
                onClick={() => setOpen(false)}
                className="block px-3 py-2 text-xs text-zinc-300 hover:bg-surface-2 transition-colors"
              >
                📈 Stats
              </Link>
              <Link
                to="/admin/users"
                onClick={() => setOpen(false)}
                className="block px-3 py-2 text-xs text-zinc-300 hover:bg-surface-2 transition-colors"
              >
                👥 Users
              </Link>
            </>
          )}
          <div className="border-t border-surface-3 my-1" />
          <button
            onClick={() => {
              logout();
              setOpen(false);
            }}
            className="block w-full text-left px-3 py-2 text-xs text-zinc-300 hover:bg-surface-2 transition-colors"
          >
            Log out
          </button>
        </div>
      )}
    </div>
  );
}
